import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { hasEditAccess } from '../permissionConfig';
import { API_BASE } from '../config';

const POLL_MS = 20000;
const SOUND_PLAYED_KEY = 'sf_so_kam_sound_played'; // sessionStorage — one beep per SO per session
const REVIEW_PATH = '/po-so-lifecycle/so-kam-review';

function getSoundPlayed() {
    try { return JSON.parse(sessionStorage.getItem(SOUND_PLAYED_KEY) || '[]'); } catch { return []; }
}
function saveSoundPlayed(list) {
    try { sessionStorage.setItem(SOUND_PLAYED_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

// Short two-tone warning beep synthesized with the Web Audio API — no audio
// asset needed. Autoplay policies may block this before the user has
// interacted with the page at all; that's fine, the red banner still alerts
// visually regardless of whether the beep actually plays.
function playWarningBeep() {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        [880, 660].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'square';
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.22);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.22 + 0.2);
            osc.connect(gain).connect(ctx.destination);
            osc.start(ctx.currentTime + i * 0.22);
            osc.stop(ctx.currentTime + i * 0.22 + 0.2);
        });
        setTimeout(() => ctx.close().catch(() => {}), 1000);
    } catch { /* ignore — visual banner is still shown */ }
}

// Mounted once at the app root (App.js), alongside EditAccessToastHost /
// UpdateBannerHost. Polls for SOs pending KAM review and, for anyone with
// edit access to SO KAM Review (any department — admin, kam, or a user
// explicitly granted edit access via the Admin Console), shows a persistent
// red banner on every page except the review page itself. Landing on that
// page is what dismisses it; navigating away while items are still pending
// brings it right back.
export function UrgentApprovalBannerHost({ user }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [pendingCount, setPendingCount] = useState(0);
    const canReview = hasEditAccess(user, 'po_so_lifecycle', 'so_kam_review');
    const pollRef = useRef(null);

    const poll = useCallback(async () => {
        try {
            const qs = `&username=${encodeURIComponent(user?.username || '')}&role=${encodeURIComponent(user?.role || '')}`;
            const res  = await fetch(`${API_BASE}/so-kam-review?action=pending&kam=${qs}`);
            const list = await res.json();
            if (!Array.isArray(list)) return;

            setPendingCount(list.length);

            const alreadyPlayed = getSoundPlayed();
            const newOnes = list.map(so => so.voucher_no).filter(v => !alreadyPlayed.includes(v));
            if (newOnes.length > 0) {
                playWarningBeep();
                saveSoundPlayed([...alreadyPlayed, ...newOnes]);
            }
        } catch (e) { console.error('Failed to poll pending SO KAM reviews', e); }
    }, [user]);

    useEffect(() => {
        if (!canReview) return;
        poll();
        pollRef.current = setInterval(poll, POLL_MS);
        return () => clearInterval(pollRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canReview]);

    if (!canReview || pendingCount === 0) return null;
    if (location.pathname.startsWith(REVIEW_PATH)) return null;

    return (
        <div style={S.wrap} onClick={() => navigate(REVIEW_PATH)}>
            <span style={S.icon}>⚠</span>
            {pendingCount} Sales Order{pendingCount !== 1 ? 's' : ''} awaiting KAM approval — click to review
        </div>
    );
}

const S = {
    wrap: {
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
        background: '#D32F2F', color: '#fff', textAlign: 'center',
        padding: '12px 16px', fontFamily: "'Poppins', sans-serif",
        fontSize: 14, fontWeight: 700, letterSpacing: '0.01em',
        cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
    },
    icon: { marginRight: 8 },
};
